import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { STORAGE_KEYS } from 'src/app/pages/distribution/helpers/constants';
import { isTokenExpired } from 'src/app/utils/token';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly distributionPathPrefix = '/api/v6/distribution';
  // The token endpoints are unauthenticated: they mint the token. A leftover
  // token must never be attached (the backend rejects a request carrying a
  // token for another order), and must never trip the expiry short-circuit
  // below, which rejects the login before it reaches the network. Either way
  // the first login attempt failed as "wrong credentials", and only the retry,
  // running against the storage this interceptor had just cleared, got through.
  private readonly authPathPrefix = '/api/v6/distribution/connect/';

  constructor(private router: Router) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(environment.appServiceUrl)) {
      return next.handle(req);
    }
    const path = req.url.slice(environment.appServiceUrl.length);
    if (!path.startsWith(this.distributionPathPrefix) || path.startsWith(this.authPathPrefix)) {
      return next.handle(req);
    }
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) {
      return next.handle(req);
    }
    if (isTokenExpired(token)) {
      this.handleSessionExpired();
      return throwError(() => new HttpErrorResponse({
        status: 401,
        statusText: 'Unauthorized',
        url: req.url,
      }));
    }
    const authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
    return next.handle(authReq).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.handleSessionExpired();
        }
        return throwError(() => err);
      })
    );
  }

  private handleSessionExpired(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ORDER_ID);
    localStorage.removeItem(STORAGE_KEYS.PAYMENT_ID);
    localStorage.removeItem(STORAGE_KEYS.ORDER_EMAIL);
    localStorage.removeItem(STORAGE_KEYS.DISTRIBUTION_DETAILS);
    this.router.navigate(['/distribution'], { queryParams: { reason: 'session-expired' } });
  }
}
